import { useState, useEffect, useRef, ReactNode } from "react"
import {
  ArrowRight,
  Sparkles,
  Brain,
  Eye,
  BarChart3,
  Zap,
  Star,
  Quote,
  ChevronDown,
  Users,
  Clock,
  TrendingUp,
  Cpu,
  Activity,
  CheckCircle,
  Target,
  FileText,
  Linkedin,
  Instagram,
  AlertTriangle,
  TrendingDown,
  DollarSign,
  FileX,
  Lightbulb,
  Plus,
  Minus,
  Plug
} from "lucide-react"
import { motion, useScroll, useTransform, useInView, useMotionValue, useSpring, useReducedMotion, AnimatePresence } from "framer-motion"
import { useNavigate, Link } from "react-router-dom"
import MetaTags from "../components/SEO/MetaTags"
import OrganizationSchema from "../components/SEO/SchemaMarkup/Organization"
import SoftwareApplicationSchema from "../components/SEO/SchemaMarkup/SoftwareApplication"
import { pageConfigs } from "../utils/seo"
import ProductBentoGrid from "../components/home/ProductBentoGrid"
import Silk from "../components/ui/Silk"
import SilkCover from "../components/ui/SilkCover"

const silkBackgroundStyle = {
  backgroundColor: "#1F3D3B",
  backgroundImage:
    "radial-gradient(70% 55% at 32% 24%, rgba(154,188,182,0.26) 0%, rgba(31,61,59,0) 60%)," +
    "radial-gradient(60% 60% at 80% 72%, rgba(47,143,191,0.12) 0%, rgba(31,61,59,0) 58%)," +
    "radial-gradient(130% 120% at 100% 0%, rgba(15,32,30,0.55) 0%, rgba(31,61,59,0) 55%)," +
    "radial-gradient(120% 120% at 0% 100%, rgba(15,32,30,0.45) 0%, rgba(31,61,59,0) 55%)," +
    "linear-gradient(160deg, #1C3836 0%, #1F3D3B 55%, #1A3230 100%)",
  backgroundAttachment: "fixed",
} as const

interface AnimatedCounterProps {
  value: number;
  duration?: number;
}

const AnimatedCounter = ({ value, duration = 2 }: AnimatedCounterProps) => {
  const [count, setCount] = useState(0)
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true })

  useEffect(() => {
    if (isInView) {
      let start = 0
      const increment = value / (duration * 60)
      const timer = setInterval(() => {
        start += increment
        if (start >= value) {
          setCount(value)
          clearInterval(timer)
        } else {
          setCount(Math.floor(start))
        }
      }, 1000 / 60)
      return () => clearInterval(timer)
    }
  }, [isInView, value, duration])

  return <span ref={ref}>{count}</span>
}

interface FloatingElementProps {
  children: ReactNode;
  delay?: number;
  duration?: number;
}

const FloatingElement = ({ children, delay = 0, duration = 20 }: FloatingElementProps) => {
  return (
    <motion.div
      initial={{ y: 0 }}
      animate={{ y: [-10, 10, -10] }}
      transition={{
        duration,
        repeat: Number.POSITIVE_INFINITY,
        ease: "easeInOut",
        delay,
      }}
    >
      {children}
    </motion.div>
  )
}

interface MagneticButtonProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}

const MagneticButton = ({ children, className = "", style, onClick }: MagneticButtonProps) => {
  const ref = useRef<HTMLButtonElement>(null)
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const springX = useSpring(x, { stiffness: 300, damping: 30 })
  const springY = useSpring(y, { stiffness: 300, damping: 30 })

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current) return
    e.stopPropagation()
    const rect = ref.current.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2
    x.set((e.clientX - centerX) * 0.1)
    y.set((e.clientY - centerY) * 0.1)
  }

  const handleMouseLeave = () => {
    x.set(0)
    y.set(0)
  }

  return (
    <motion.button
      ref={ref}
      style={{ x: springX, y: springY, ...style }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      className={className}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      role="button"
      tabIndex={0}
    >
      {children}
    </motion.button>
  )
}

interface ParallaxSectionProps {
  children: ReactNode;
  offset?: number;
}

const ParallaxSection = ({ children, offset = 50 }: ParallaxSectionProps) => {
  const { scrollYProgress } = useScroll()
  const y = useTransform(scrollYProgress, [0, 1], [0, offset])

  return (
    <motion.div 
      style={{ y, willChange: 'transform' }} 
      className="relative z-10" 
      role="main"
    >
      {children}
    </motion.div>
  )
}

interface SectionWindowProps {
  children: ReactNode;
  className?: string;
  windowClassName?: string;
  id?: string;
}

const SectionWindow = ({ children, className = "", windowClassName = "", id }: SectionWindowProps) => (
  <section id={id} className={`py-10 sm:py-14 px-5 sm:px-8 md:px-10 lg:px-12 scroll-mt-24 ${className}`}>
    <div
      className={`w-full mx-auto fownd-window-surface bg-dots rounded-[1.5rem] sm:rounded-[1.75rem] lg:rounded-[2rem] py-14 sm:py-16 lg:py-20 px-6 sm:px-10 lg:px-14 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08),0_12px_32px_-8px_rgba(0,0,0,0.12)] ${windowClassName}`}
      style={{ userSelect: "text" }}
    >
      {children}
    </div>
  </section>
)

interface ModernCardProps {
  children: ReactNode;
  className?: string;
  span?: string;
  variant?: 'dark' | 'light' | 'gradient';
}

const ModernCard = ({ children, className = "", span = "col-span-1", variant = "dark" }: ModernCardProps) => {
  const cardStyles = {
    dark: "bg-[#1F3D3B] text-white",
    light: "bg-white text-gray-900",
    gradient: "bg-gradient-to-br from-[#619ed3] to-[#5c91a4] text-white",
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -8 }}
      transition={{ duration: 0.4 }}
      className={`${span} ${className} h-full`}
    >
      <div
        className={`${cardStyles[variant]} h-full shadow-[0_10px_40px_-15px_rgba(0,0,0,0.1)] backdrop-blur-xl transition-all duration-500 group overflow-hidden relative rounded-2xl hover:shadow-[0_20px_50px_-15px_rgba(0,0,0,0.15)]`}
      >
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#619ed3]/5 via-transparent to-[#5c91a4]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {/* Content Container */}
        <div className="p-8 relative z-10 h-full">
          {children}
        </div>
      </div>
    </motion.div>
  );
};

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  location: string;
  rating: number;
  avatar: string;
}

interface Feature {
  icon: React.ElementType;
  title: string;
  description: string;
  benefits: string[];
  metric: string;
  gradient: string;
  comingSoon?: boolean;
}

interface Stat {
  icon: React.ElementType;
  value: string | number;
  suffix: string;
  label: string;
  color: string;
  audience: string;
}

interface FAQItemProps {
  question: string;
  answer: string;
}

const FAQItem = ({ question, answer }: FAQItemProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 overflow-hidden hover:border-white/30 transition-all duration-300 shadow-lg hover:shadow-xl">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-white/10 transition-all duration-300 group"
      >
        <h3 className="text-lg font-semibold text-white font-productSans pr-4 group-hover:text-blue-200 transition-colors duration-300">
          {question}
        </h3>
        <div className="flex-shrink-0">
          {isOpen ? (
            <Minus className="w-5 h-5 text-blue-300 group-hover:text-blue-200 transition-colors duration-300" />
          ) : (
            <Plus className="w-5 h-5 text-blue-300 group-hover:text-blue-200 transition-colors duration-300" />
          )}
        </div>
      </button>
      
      <motion.div
        initial={false}
        animate={{
          height: isOpen ? "auto" : 0,
          opacity: isOpen ? 1 : 0
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="overflow-hidden"
      >
        <div className="px-6 pb-5 pt-0">
          <p className="text-slate-300 leading-relaxed text-base">
            {answer.split('**').map((part, index) => 
              index % 2 === 1 ? (
                <strong key={index} className="font-semibold text-blue-200">{part}</strong>
              ) : (
                part
              )
            )}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default function FowndLanding() {
  const { scrollYProgress } = useScroll()
  const [isLoaded, setIsLoaded] = useState(false)
  const prefersReducedMotion = useReducedMotion()

  const navigate = useNavigate()

  const carouselRef = useRef<HTMLDivElement | null>(null);

  const testimonialCarouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsLoaded(true)
  }, [])

  const stats: Stat[] = [
    { icon: Clock, value: "2-3", suffix: "hrs", label: "Documentation Time Saved Daily", color: "#619ed3", audience: "PT" },
    { icon: Users, value: 50, suffix: "%", label: "Error Reduction in Note Compliance", color: "#619ed3", audience: "PT" },
    { icon: TrendingUp, value: "50-70", suffix: "k", label: "Savings in Attrition Costs", color: "#619ed3", audience: "Clinic" },
    { icon: Target, value: "3-5", suffix: "x", label: "Potential ROI Improvement", color: "#619ed3", audience: "Clinic" },
  ]

  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Testimonials carousel state
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const testimonialIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Check if screen is mobile
  useEffect(() => {
    const checkScreenSize = () => {
      const newIsMobile = window.innerWidth < 1024;
      if (newIsMobile !== isMobile) {
        setCurrentIndex(0);
        setCurrentTestimonialIndex(0);
        setIsMobile(newIsMobile);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, [isMobile]);

  // Handle auto-scroll only on mobile
  useEffect(() => {
    if (!isMobile) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % stats.length);
    }, 3000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (testimonialIntervalRef.current) {
        clearInterval(testimonialIntervalRef.current);
        testimonialIntervalRef.current = null;
      }
    };
  }, [isMobile, stats.length]);

  const features: Feature[] = [
    {
      icon: Brain,
      title: "Documentation, Powered by AI",
      description:
        "Notation's intelligent documentation platform listens during the session, understands clinical context, and builds a complete SOAP note as you go",
      benefits: [
        "Real-Time Processing summarizes patient history and session conversations",
        "Medical large language models identify what's clinically relevant and appropriate",
        "Acts as a documentation assistant, reducing the administrative load on physical therapists",
      ],
      metric: "Built to save up to 2-3 hours a day",
      gradient: "from-[#619ed3] to-[#5c91a4]",
    },
    {
      icon: Eye,
      title: "Machine Learning and Computer Vision Analytics",
      description:
        "High-quality audio capture and video motion analysis (coming soon), all accessible on an everyday laptop",
      benefits: [
        "Run the application adjacent to your EMR on Chrome, Safari or Edge",
        "Nothing more needed than the camera and microphone built into the laptop",
        "Accurately capture, analyze, and track a patient's movement overtime",
      ],
      metric: "Reduce documentation errors up to 50%",
      gradient: "from-[#5c91a4] to-[#495B25]",
      comingSoon: true,
    },
    {
      icon: TrendingUp,
      title: "Predictive Intelligence Designed for Physical Therapists",
      description: "Smart billing optimization and treatment planning assistance",
      benefits: [
        "Automated billing codes that can be modified to meet your needs",
        "Suggested interventions, with a built in HEP creator",
        "SMART goal setting that meets insurance requirements",
      ],
      metric: "Save $50-70k in attrition costs",
      gradient: "from-[#495B25] to-[#619ed3]",
    },
    {
      icon: Cpu,
      title: "PT-Enabled Feedback Loop",
      description: "AI-powered documentation that adapts to your unique clinical style",
      benefits: [
        "Each note stays fully customizable by the therapist",
        "Adaptive technology learns your preferences through every change you make",
        "HIPAA-compliant, with privacy built in from the start",
      ],
      metric: "Reduce PT Burnout",
      gradient: "from-[#619ed3] to-[#495B25]",
    },
  ]

  const testimonials: Testimonial[] = [
    {
      quote:
        "With your vision, you have the opportunity here to standardize care across the industry, which would then allow us to have real data and efficacy measures.",
      author: "Performance PT Clinic, CA, USA",
      role: "",
      location: "",
      rating: 5,
      avatar: "/placeholder.svg?height=60&width=60",
    },
    {
      quote:
        "That is beyond awesome. That is what PT needs to improve efficiency, improving billing and coding accuracy.",
      author: "Regional Manager PT Clinics, Indiana",
      role: "",
      location: "",
      rating: 5,
      avatar: "/placeholder.svg?height=60&width=60",
    },
    {
      quote:
        "That is the best AI EMR I have ever seen. Through all my research there has not been that level of focus and purpose behind the design of a EMR and SOAP note.",
      author: "IT Executive, Physical Therapy Partners",
      role: "",
      location: "",
      rating: 5,
      avatar: "/placeholder.svg?height=60&width=60",
    },
  ]

  // Handle testimonials auto-scroll only on mobile
  useEffect(() => {
    if (!isMobile) {
      if (testimonialIntervalRef.current) {
        clearInterval(testimonialIntervalRef.current);
        testimonialIntervalRef.current = null;
      }
      return;
    }

    // Clear any existing interval
    if (testimonialIntervalRef.current) {
      clearInterval(testimonialIntervalRef.current);
    }

    // Start new interval
    testimonialIntervalRef.current = setInterval(() => {
      setCurrentTestimonialIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
    }, 4000); // 4 seconds for testimonials

    return () => {
      if (testimonialIntervalRef.current) {
        clearInterval(testimonialIntervalRef.current);
        testimonialIntervalRef.current = null;
      }
    };
  }, [isMobile, testimonials.length]);

  return (
    <>
      <MetaTags
        title={pageConfigs.home.title}
        description={pageConfigs.home.description}
        keywords={pageConfigs.home.keywords}
        canonical={pageConfigs.home.canonical}
      />
      <OrganizationSchema />
      <SoftwareApplicationSchema />
      
    <div className="min-h-screen overflow-hidden relative isolate" style={{ backgroundColor: "#1F3D3B", pointerEvents: "auto" }}>
      {/* Animated silk background — fixed behind all content */}
      <div className="fixed inset-0 -z-10 pointer-events-none" aria-hidden="true">
        {prefersReducedMotion ? (
          <div className="absolute inset-0" style={silkBackgroundStyle} />
        ) : (
          <Silk speed={2.7} scale={0.7} color="#1F3D3B" noiseIntensity={0.6} rotation={1.64} />
        )}
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 overflow-hidden pb-24">

        <ParallaxSection offset={100}>
          <div className="max-w-6xl mt-24 mx-auto text-center relative z-20">
            <AnimatePresence>
              {isLoaded && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1 }}
                  className="space-y-10"
                >
                  <motion.div
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="pt-8"
                  >
                    <motion.h1
                      initial={{ y: 50, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.8, delay: 0.4 }}
                      className="mt-2 font-bold font-productSans leading-tight tracking-tight"
                    >
                      <span className="font-productSans text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold block">
                        <span>
                          <span className="fownd-glow" data-text="Fownd">
                            Fownd
                          </span>
                          ed
                        </span> by Clinicians
                        <span className="block mt-2 sm:mt-3">for Clinicians</span>
                      </span>
                    </motion.h1>

                    <motion.div
                      initial={{ y: 30, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.8, delay: 0.6 }}
                      className="inline-flex items-center text-white text-lg sm:text-xl font-semibold font-productSans mt-4 sm:mt-5"
                    >
                      <Sparkles className="hidden sm:inline-block w-5 h-5 mr-2 text-[#619ed3]" />
                      Building Tomorrow's Care — One Session at a Time
                    </motion.div>
                  </motion.div>

                  {/* Hero CTAs */}
                  <motion.div
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.8 }}
                    className="flex flex-col sm:flex-row gap-5 justify-center items-center"
                  >
                    <a href="https://notation.fownd.care/" target="_blank" rel="noopener noreferrer">
                      <button className="group relative w-[220px] sm:w-[240px] text-white px-8 py-4 text-base rounded-full font-semibold transition-all duration-300 transform hover:scale-110 hover:shadow-2xl bg-gradient-to-r from-[#619ed3] to-[#5c91a4] hover:from-[#3a8bb8] hover:to-[#4f8291] cursor-pointer">
                        <span className="flex items-baseline justify-center gap-2">
                          <span>
                            Start{" "}
                            <span
                              className="leading-none"
                              style={{ fontFamily: "'Baloo Bhaina 2', sans-serif", fontSize: "1.1em" }}
                            >
                              notation
                            </span>
                          </span>
                          <ArrowRight className="w-5 h-5 self-center group-hover:translate-x-2 transition-transform duration-300" />
                        </span>
                        <div className="absolute inset-0 bg-white/30 rounded-full blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </button>
                    </a>

                    <MagneticButton
                      onClick={() =>
                        document
                          .getElementById("solutions")
                          ?.scrollIntoView({ behavior: "smooth" })
                      }
                      className="group w-[220px] sm:w-[240px] bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 hover:border-white/40 px-8 py-4 text-base rounded-full transition-all duration-300 cursor-pointer">
                      <span className="flex items-center justify-center gap-2">
                        Explore Solutions
                        <ChevronDown className="w-5 h-5 group-hover:translate-y-0.5 transition-transform duration-300" />
                      </span>
                    </MagneticButton>
                  </motion.div>

                  {/* Scroll Indicator */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.5 }}
                    className="flex justify-center mt-12 relative z-30"
                  >
                    <motion.button
                      type="button"
                      aria-label="Scroll to solutions"
                      animate={{ y: [0, 10, 0] }}
                      transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
                      className="text-white/60 hover:text-white cursor-pointer p-3 -m-1"
                      onClick={() => {
                        document
                          .getElementById("solutions")
                          ?.scrollIntoView({ behavior: "smooth" });
                      }}
                    >
                      <ChevronDown className="w-6 h-6" />
                    </motion.button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </ParallaxSection>
      </section>

      {/* Product ecosystem */}
      <SectionWindow id="solutions">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-14 select-text"
        >
          <div className="inline-flex items-center text-[#495B25] text-base mb-6 font-semibold font-productSans select-text">
            <Zap className="w-4 h-4 mr-2" />
            How It Works
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 tracking-tight text-gray-900 font-productSans select-text">
            <span className="select-text">See How </span>
            <span className="text-[#1F3D3B] select-text">Fownd</span>
            <span className="select-text"> Transforms Care</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto font-light leading-relaxed select-text">
            One ecosystem for clinical documentation, developer APIs, EMR integration, and anatomy education.
          </p>
        </motion.div>

        <ProductBentoGrid />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="flex items-center w-full mb-8">
            <div className="flex-grow h-px bg-gray-300" />
            <div className="px-8 flex-shrink-0">
              <span className="text-gray-500 text-md font-medium font-dmSans">Program Partners</span>
            </div>
            <div className="flex-grow h-px bg-gray-300" />
          </div>

          <div className="flex items-center justify-center gap-16 md:gap-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <img src="/assets/images/home/aws-activate.png" alt="AWS Activate" className="h-16 md:h-20 w-auto object-contain" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.45 }}
            >
              <img
                src="/assets/images/home/nvidia-inception-program.png"
                alt="NVIDIA Inception Program"
                className="h-16 md:h-20 w-auto object-contain"
              />
            </motion.div>
          </div>
        </motion.div>
      </SectionWindow>

      {/* ROI Calculator - New Design - Moved back to original position */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto w-full relative z-10 p-6 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Content */}
          <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="space-y-6"
            >
              <div className="space-y-3">
                <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight font-productSans">
                  Calculate Your
                  <span className="block bg-gradient-to-r from-[#58ACDD] to-[#486633] bg-clip-text text-transparent">
                    ROI Impact
                  </span>
            </h2>
                <p className="text-lg text-white/80 leading-relaxed max-w-lg font-dmSans">
                  Discover the transformative financial impact of Fownd's AI solutions on your practice. See real
                  savings from day one.
            </p>
              </div>
              <MagneticButton
                onClick={() => navigate('/roi-calculator')}
                className="group relative text-white px-6 py-3 rounded-full font-semibold text-base transition-all duration-300 transform hover:scale-105 hover:shadow-2xl font-dmSans bg-gradient-to-r from-[#58ACDD] to-[#486633] hover:from-[#58ACDD]/90 hover:to-[#486633]/90 cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  Launch ROI Calculator
                  <TrendingUp className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </span>
                <div className="absolute inset-0 bg-white/20 rounded-full blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </MagneticButton>
          </motion.div>

            {/* Right Stats Card - Organized by Audience */}
          <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.3 }}
              className="space-y-6"
          >
              {/* For Physical Therapists */}
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/10 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#58ACDD]/10 to-[#486633]/10"></div>
                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-[#58ACDD]/20 to-[#486633]/20 rounded-full blur-xl"></div>

                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-[#58ACDD]/20 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-[#58ACDD]" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                      </svg>
                    </div>
                    <h3 className="text-white font-semibold text-lg font-productSans">For Clinicians</h3>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {stats.filter(stat => stat.audience === "PT").map((stat, index) => (
                      <div key={index} className="text-center group cursor-pointer" onClick={() => navigate('/roi-calculator')}>
                        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 hover:bg-white/10 transition-all duration-300 border border-white/10 hover:border-[#58ACDD]/30 h-full flex flex-col justify-center">
                          <stat.icon className="w-5 h-5 text-[#58ACDD] mx-auto mb-2 group-hover:scale-110 transition-transform duration-300" />
                          <div className="text-xl font-bold text-white mb-1 font-productSans">
                            {typeof stat.value === "string" ? stat.value : <AnimatedCounter value={stat.value} />}
                            <span className="text-lg">{stat.suffix}</span>
                        </div>
                          <div className="text-white/70 text-xs font-dmSans leading-tight">{stat.label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                      </div>
                    </div>

              {/* For Clinic Owners */}
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/10 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#486633]/10 to-[#58ACDD]/10"></div>
                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-[#486633]/20 to-[#58ACDD]/20 rounded-full blur-xl"></div>

                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-[#58ACDD]/20 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-[#58ACDD]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h4a2 2 0 012 2v2a2 2 0 01-2 2H8a2 2 0 01-2-2v-2z" clipRule="evenodd" />
                      </svg>
                        </div>
                    <h3 className="text-white font-semibold text-lg font-productSans">For Clinic Owners</h3>
                      </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {stats.filter(stat => stat.audience === "Clinic").map((stat, index) => (
                      <div key={index} className="text-center group cursor-pointer" onClick={() => navigate('/roi-calculator')}>
                        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 hover:bg-white/10 transition-all duration-300 border border-white/10 hover:border-[#58ACDD]/30 h-full flex flex-col justify-center">
                          <stat.icon className="w-5 h-5 text-[#58ACDD] mx-auto mb-2 group-hover:scale-110 transition-transform duration-300" />
                          <div className="text-xl font-bold text-white mb-1 font-productSans">
                            {typeof stat.value === "string" ? stat.value : <AnimatedCounter value={stat.value} />}
                            <span className="text-lg">{stat.suffix}</span>
                    </div>
                          <div className="text-white/70 text-xs font-dmSans leading-tight">{stat.label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
              </div>
            </div>
          </motion.div>
        </div>
        </div>
      </section>

      {/* Testimonials - Light Gray Background - Moved back to original position */}
      <SectionWindow>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
            className="text-center mb-16 select-text"
        >
          <div className="inline-flex items-center text-[#619ed3] text-base mb-6 font-semibold font-productSans select-text">
            <Sparkles className="w-4 h-4 mr-2" />
            Testimonials
          </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 tracking-tight text-gray-900 font-productSans select-text">
              <span className="select-text">What Clinicians are Saying about{" "}</span>
              <span className="text-[#439CCB] select-text" style={{ fontFamily: "'Baloo Bhaina 2', sans-serif", fontSize: '1.2em' }}>
              notation
            </span>
          </h2>
        </motion.div>

        {/* Mobile Carousel */}
        {isMobile && (
          <div className="lg:hidden relative overflow-hidden">
            <div
              ref={testimonialCarouselRef}
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentTestimonialIndex * 100}%)` }}
            >
              {testimonials.map((testimonial, index) => (
                <div key={index} className="min-w-full flex justify-center items-center px-4">
                  <div className="w-full max-w-sm">
                    <ModernCard variant="light">
                      <div className="flex flex-col h-full">
                        <div className="flex mb-5">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={i} className="w-5 h-5 fill-current" style={{ color: "#daa520" }} />
                          ))}
                        </div>

                        <Quote className="w-10 h-10 text-[#5c91a4] mb-5" />

                        <p className="text-gray-700 mb-6 leading-relaxed italic text-sm flex-grow select-text">
                          "{testimonial.quote}"
                        </p>

                        <div className="mt-auto">
                          <p className="text-gray-800 font-medium select-text">{testimonial.author}</p>
                        </div>
                      </div>
                    </ModernCard>
                  </div>
                </div>
              ))}
            </div>

            {/* Carousel Dots */}
            <div className="flex justify-center mt-8 gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonialIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentTestimonialIndex
                    ? 'bg-[#1e3d3b] scale-125'
                    : 'bg-gray hover:bg-gray-400'
                    }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Desktop Grid */}
        <div className="hidden lg:grid lg:grid-cols-3 gap-6 lg:gap-8 justify-center max-w-6xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.2 }}
            >
              <ModernCard variant="light">
                <div className="flex flex-col h-full">
                  <div className="flex mb-5">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-current" style={{ color: "#daa520" }} />
                    ))}
                  </div>

                  <Quote className="w-10 h-10 text-[#5c91a4] mb-5" />

                  <p className="text-gray-700 mb-6 leading-relaxed italic text-sm sm:text-base flex-grow select-text">
                    "{testimonial.quote}"
                  </p>

                  <div className="mt-auto">
                    <p className="text-gray-800 font-medium select-text">{testimonial.author}</p>
                  </div>
                </div>
              </ModernCard>
            </motion.div>
          ))}
        </div>
      </SectionWindow>

      {/* Blog Section - Light Gray Background */}
      <SectionWindow>
        <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="text-[#1f3d3b] text-sm font-semibold mb-4 tracking-wider uppercase font-productSans">
              SOCIAL MEDIA
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 tracking-tight text-gray-900 font-productSans">
              Latest Updates
            </h2>
            <p className={`text-lg text-gray-600 max-w-2xl mx-auto font-light leading-relaxed`}>
              Stay informed about our latest developments and insights
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Blog Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl overflow-hidden shadow-[0_10px_40px_-15px_rgba(0,0,0,0.1)] hover:shadow-[0_20px_50px_-15px_rgba(0,0,0,0.15)] transition-all duration-300 group"
            >
              <SilkCover base="#1F3D3B" className="h-48 flex items-center justify-center">
                <div className="relative z-10 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
              </SilkCover>
              <div className="p-6">
                <h3
                  className={`text-xl font-bold text-[#1e3d3b] mb-3 group-hover:text-[#1e3d3b]/80 transition-colors font-productSans`}>
                  Our Blog
                </h3>
                <p className={`text-gray-600 text-sm mb-4 leading-relaxed font-dmSans`}>
                  Discover our latest articles, insights and updates about AI in healthcare documentation.
                </p>
                <div className="flex items-center justify-between">
                  <span className={`text-gray-500 text-sm font-dmSans`}>{new Date().toLocaleDateString()}</span>
                  <button
                    onClick={() => navigate("/blogs")}
                    className={`text-[#1f3d3b] text-sm font-medium hover:text-[#1e3d3b]/80 transition-colors flex items-center gap-1 font-dmSans cursor-pointer`}
                  >
                    Read Blogs <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </motion.div>

            {/* LinkedIn Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl overflow-hidden shadow-[0_10px_40px_-15px_rgba(0,0,0,0.1)] hover:shadow-[0_20px_50px_-15px_rgba(0,0,0,0.15)] transition-all duration-300 group"
            >
              <SilkCover base="#429CCB" className="h-48 flex items-center justify-center">
                <div className="relative z-10 w-12 h-12 bg-white/25 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <Linkedin className="w-6 h-6 text-white" />
                </div>
              </SilkCover>
              <div className="p-6">
                <h3
                  className={`text-xl font-bold text-[#1e3d3b] mb-3 group-hover:text-[#1e3d3b]/80 transition-colors font-productSans`}>
                  LinkedIn Posts
                </h3>
                <p className={`text-gray-600 text-sm mb-4 leading-relaxed font-dmSans`}>
                  Follow our LinkedIn for the latest updates, industry insights and company announcements.
                </p>
                <div className="flex items-center justify-between">
                  <span className={`text-gray-500 text-sm font-dmSans`}>{new Date().toLocaleDateString()}</span>
                  <a
                    href="https://www.linkedin.com/company/fownd-inc/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-black text-sm font-medium hover:text-gray-700 visited:text-black transition-colors flex items-center gap-1 font-dmSans">
                    View Posts <ArrowRight className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Instagram Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl overflow-hidden shadow-[0_10px_40px_-15px_rgba(0,0,0,0.1)] hover:shadow-[0_20px_50px_-15px_rgba(0,0,0,0.15)] transition-all duration-300 group"
            >
              <SilkCover base="#f2eacb" className="h-48 flex items-center justify-center">
                <div className="relative z-10 w-12 h-12 bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <Instagram className="w-6 h-6 text-[#1f3d3b]" />
                </div>
              </SilkCover>
              <div className="p-6">
                <h3
                  className={`text-xl font-bold text-[#1e3d3b] mb-3 group-hover:text-[#1e3d3b]/80 transition-colors font-productSans`}>
                  Instagram Posts
                </h3>
                <p className={`text-gray-600 text-sm mb-4 leading-relaxed font-dmSans`}>
                  Check out our Instagram for visual stories, team highlights and behind-the-scenes content.
                </p>
                <div className="flex items-center justify-between">
                  <span className={`text-gray-500 text-sm font-dmSans`}>{new Date().toLocaleDateString()}</span>
                  <Link
                    to="/instagram"
                    className="text-black text-sm font-medium hover:text-gray-700 visited:text-black transition-colors flex items-center gap-1 font-dmSans">
                    View Posts <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>

          {/* View All Posts Button */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <button
              onClick={() => navigate("/blogs")}
              className="cursor-pointer inline-flex items-center justify-center border-2 border-[#1e3d3b] text-[#1e3d3b] hover:bg-[#1e3d3b] hover:text-white px-8 py-3 rounded-full font-medium gap-2"
            >
              View All Posts <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
      </SectionWindow>

      {/* Modern FAQ Section - Compact Grid Design */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-productSans">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-200 max-w-2xl mx-auto leading-relaxed">
              Everything you need to know about FOWND
            </p>
          </motion.div>

          {/* Two-Column Grid for Desktop, Single Column for Mobile */}
          <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {[
              {
                question: "What is FOWND?",
                answer: "FOWND is a company built by physical therapists to solve real clinical problems using AI—starting with documentation."
              },
              {
                question: "Who is FOWND for?",
                answer: "FOWND is designed for outpatient physical therapy clinics of all sizes—from single providers to enterprise-level groups."
              },
              {
                question: "What makes FOWND different?",
                answer: "We're PTs ourselves. We understand the unique needs of rehab therapy and build tools that reflect clinical nuance and real-world workflow."
              },
              {
                question: "Is FOWND only for physical therapy?",
                answer: "Our first tool, Notation, is purpose-built for physical therapists. We're exploring future support for OT and SLP."
              },
              {
                question: "Is FOWND HIPAA-compliant?",
                answer: "Yes. FOWND fully complies with HIPAA regulations to ensure your clinical data and patient records remain secure and protected."
              },
              {
                question: "Can I customize my SOAP note templates?",
                answer: "Absolutely. You can easily customize SOAP note templates to fit your clinic's unique documentation workflows and preferences."
              },
              {
                question: "How does our AI medical scribe work?",
                answer: "FOWND creates real-time SOAP notes built for PT—goals, HEPs, codes, and more—directly in your browser, no installs or EMR integration required. Therapists shape notes as they’re drafted, while FOWND adapts to your clinic’s workflow, improving speed, accuracy, and ease with every session."
              },
              {
                question: "Works for solo PTs and larger clinics?",
                answer: "Both. Whether you're a solo practitioner or a multi-provider clinic, FOWND scales to your needs and helps streamline documentation."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 hover:bg-white/10 group"
              >
                <h3 className="text-white font-semibold text-lg mb-3 font-productSans group-hover:text-[#619ed3] transition-colors duration-300">
                  {faq.question}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed font-dmSans">
                  {faq.answer}
                </p>
          </motion.div>
            ))}
        </div>
        </div>
      </section>

      {/* CTA Section - Dark Background */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-white/5 border border-white/10 backdrop-blur-xl shadow-2xl overflow-hidden relative rounded-2xl">
              <div className="absolute inset-0 bg-gradient-to-r from-[#619ed3]/5 to-[#5c91a4]/5" />
              <div className="p-16 relative z-10">
                <motion.h2
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 tracking-tight text-white font-productSans">
                  Ready to{" "}
                  <span className="bg-gradient-to-r from-[#619ed3] to-[#5c91a4] bg-clip-text text-transparent">
                    Transform
                  </span>{" "}
                  Your Practice?
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className={`text-lg text-white/80 mb-10 max-w-2xl mx-auto font-light leading-relaxed`}>
                  Join the future of clinical documentation with Fownd's revolutionary AI-powered solutions
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                  className="flex flex-col sm:flex-row gap-5 justify-center"
                >
                  <a href="https://notation.fownd.care/" target="_blank" rel="noopener noreferrer">

                    <MagneticButton
                      className={`bg-gradient-to-r from-[#619ed3] to-[#5c91a4] hover:from-[#3a8bb8] hover:to-[#4f8291] text-white px-8 py-4 text-base rounded-full shadow-2xl`}>
                      <span className="flex items-center gap-2">
                        Try Notation Free
                        <ArrowRight className="w-5 h-5" />
                      </span>
                    </MagneticButton>
                  </a>

                  <a
                    href="https://bookings.cloud.microsoft/book/NotationAI@fownd.org/?ismsaljsauthenabled"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MagneticButton
                      className={`border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 text-base rounded-full`}>
                      Schedule Consultation
                    </MagneticButton>
                  </a>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section >
    </div >
    </>
  )
}
